/* eslint-disable no-console */
import React, { useState, useContext } from 'react'
import { Link, navigate, graphql } from 'gatsby'
import { CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import BigNumber from 'bignumber.js'
import { useTranslation } from 'react-i18next'

import Verify from '~components/Verify'
import RegisterLayout from '~components/RegisterLayout'
import SubmitButton from '~components/SubmitButton'
import InputField from '~components/InputField'

import {
  usernameOrEmailSchema,
  passwordSchema,
  errors,
} from '~utils/validationSchema'
import userPool from '~utils/cognito'
import { fetchWallet } from '~utils/fetchWallet'
import { fetchWalletBalance } from '~utils/fetchBalance'
import { UserContext } from '~context/provider'
import * as s from './signin.module.scss'

const Signin = () => {
  const { t } = useTranslation()
  const { setUser } = useContext(UserContext)
  const [loading, setLoading] = useState(false)
  const [showVerify, setShowVerify] = useState(false)

  const handleSubmit = (values, setErrors) => {
    setLoading(true)
    const username = values.username.trim()
    const { password } = values

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    })

    const authenticationDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    })

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        const accessToken = session.getAccessToken().getJwtToken()
        const idToken = session.getIdToken().getJwtToken()
        const refreshToken = session.getRefreshToken().getToken()

        cognitoUser.getUserAttributes(async (err, result) => {
          if (err) {
            console.log(
              '%c error on get cognito user on sign in',
              'color: red',
              err
            )
            setLoading(false)
            return
          }
          console.log('%c success:', 'color: green', result)

          const addresses = await fetchWallet(accessToken)
          const ethereumAddress = `0x${addresses.ethereum}`

          const balance = await fetchWalletBalance(ethereumAddress)

          setUser({
            username,
            email: result.find((attr) => attr.Name === 'email').Value,
            accessToken,
            idToken,
            refreshToken,
            wallets: addresses,
            ethereumAddress,
          })

          setLoading(false)

          if (new BigNumber(balance).isGreaterThan(0)) {
            navigate('/dashboard')
          } else {
            navigate('/buy-tokens')
          }
        })
      },
      onFailure: (err) => {
        console.log('%c login failed: ', 'color: red', err)
        setLoading(false)
        switch (err.code) {
          case errors.USER_NOT_FOUND:
            setErrors({ username: 'Username not found' })
            break
          case errors.INCORRECT_PASSWORD:
            setErrors({ password: 'Incorrect password' })
            break
          case errors.USER_NOT_CONFIRMED:
            navigate('/confirm')
            break
          default:
            setErrors({ username: `An error occurred: ${err.code}` })
            break
        }
      },
      totpRequired: () => {
        setShowVerify(true)
        setLoading(false)
      },
    })
  }

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: Yup.object({
      username: usernameOrEmailSchema,
      password: passwordSchema,
    }),
    onSubmit: (values) => {
      handleSubmit(values, formik.setErrors)
    },
  })

  if (showVerify) {
    return <Verify user={formik.values} />
  }

  return (
    <RegisterLayout title={t('signInTitle')}>
      <div className={s.formContainer}>
        <form className={s.form} onSubmit={formik.handleSubmit}>
          <p className={s.formDescription}>{t('signInDescription')}</p>
          <p className={s.newUser}>
            {t('newUser')} <Link to="/signup">{t('createAccount')}</Link>
          </p>
          <InputField
            id="username"
            name="username"
            type="text"
            labelText="Username or Email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.username}
            error={formik.touched.username && formik.errors.username}
          />
          <InputField
            id="password"
            name="password"
            type="password"
            labelText="Password"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.password}
            error={formik.touched.password && formik.errors.password}
          />
          <SubmitButton title={t('continue')} loading={loading} />
        </form>
        <Link to="/recovery" className={s.userLink}>
          {t('forgotPassword')}
        </Link>
      </div>
    </RegisterLayout>
  )
}

export default Signin

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(
      filter: { ns: { in: ["common", "signin"] }, language: { eq: $language } }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
